// @ts-nocheck
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, ClipboardList, BadgeCheck, ArrowRight } from "lucide-react";

const steps = [
  {
    icon:        <Search size={22} />,
    number:      "01",
    title:       "Browse & Choose",
    description:
      "Explore IKA's international training programs by category, field, or destination. Find the program that matches your organization's goals, leadership needs, and professional development objectives.",
  },
  {
    icon:        <ClipboardList size={22} />,
    number:      "02",
    title:       "Submit Your Registration",
    description:
      "Complete the online registration form with participant and institutional details. Our team is designed for professionals, executives, government employees, and institutional leaders — no account required.",
  },
  {
    icon:        <BadgeCheck size={22} />,
    number:      "03",
    title:       "Attend, Apply & Get Certified",
    description:
      "Join your international program, engage in expert-led learning and practical workshops, then return to your organization with practical tools, renewed perspectives, and a documented IKA professional certificate.",
  },
];

/* ─── Animation variants ───────────────────────────────────────────────────── */

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay },
  }),
};

const container = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariant = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

/* ─── Section ──────────────────────────────────────────────────────────────── */

const HowToEnroll = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-24 px-6 bg-slate-50 overflow-hidden">

      {/* Decorative blobs */}
      <div className="absolute -top-24 left-1/3  w-[480px] h-[480px] bg-gold-100/40  rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0  right-1/4 w-[360px] h-[360px] bg-navy-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16 space-y-5">

          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="flex justify-center"
          >
            <span className="inline-flex items-center gap-2 bg-gold-50 border border-gold-200 text-gold-600 text-[11px] font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-full">
              Simple Process
            </span>
          </motion.div>

          <motion.h2
            custom={0.08}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="text-3xl md:text-5xl font-extrabold text-navy-800 leading-tight tracking-tight"
          >
            Enroll in{" "}
            <span className="bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 bg-clip-text text-transparent">
              3 Simple Steps
            </span>
          </motion.h2>

          <motion.p
            custom={0.16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="text-slate-500 max-w-lg mx-auto text-sm md:text-base leading-relaxed"
          >
            Joining an IKA international training program is simple. Here's how
            professionals and institutions take the first step toward measurable growth.
          </motion.p>

          <motion.div
            custom={0.22}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="flex justify-center"
          >
            <div className="w-16 h-[3px] rounded-full bg-gradient-to-r from-gold-400 to-gold-600" />
          </motion.div>
        </div>

        {/* Steps */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          variants={container}
        >
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              variants={cardVariant}
              className="relative bg-white border border-slate-100 rounded-2xl p-8 flex flex-col gap-5 hover:border-gold-200 hover:shadow-[0_8px_32px_rgba(201,168,76,0.10)] transition-all duration-300"
            >
              {/* Connector line to next step (desktop only) */}
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-10 left-[calc(100%+1px)] w-8 h-px bg-gradient-to-r from-gold-300 to-transparent pointer-events-none" />
              )}

              {/* Step number badge + icon */}
              <div className="flex items-center gap-4">
                <div className="relative w-12 h-12 rounded-xl bg-navy-50 border border-navy-100 flex items-center justify-center text-navy-600 flex-shrink-0">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gold-500 text-white text-[10px] font-extrabold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <span className="text-5xl font-black text-slate-100 leading-none select-none">
                  {step.number}
                </span>
              </div>

              {/* Text */}
              <div>
                <h3 className="text-navy-800 font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed text-justify">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <button
            onClick={() => navigate("/programs")}
            className="inline-flex items-center gap-2 bg-navy-800 hover:bg-navy-700 text-white font-bold text-sm px-10 py-3.5 rounded-md lg:rounded-lg transition-colors duration-200 shadow-sm"
          >
            Browse Programs & Register
            <ArrowRight size={16} />
          </button>
          <p className="text-slate-400 text-xs mt-3">No account required to register</p>
        </motion.div>

      </div>
    </section>
  );
};

export default HowToEnroll;
